import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useBrevoContacts } from '../../queries/brevoContacts';
import { useBrevoLists } from '../../queries/brevoLists';

const PAGE_SIZE = 50;

// Read-only, matching the old admin exactly - it never had create/update/delete
// contact routes. Search is client-side over the currently loaded page only
// (no server-side contact search exists to port).
export function BrevoContactsPage() {
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const { data, isLoading } = useBrevoContacts(PAGE_SIZE, offset);
  // Map list id → name so the Lists column reads as names, not bare IDs.
  const { data: lists = [] } = useBrevoLists({ all: true });
  const listName = useMemo(() => new Map(lists.map((l) => [l.id, l.name])), [lists]);

  const contacts = (data?.contacts ?? []).filter((contact) => {
    if (!search) return true;
    const lower = search.toLowerCase();
    const firstName = String(contact.attributes.FIRSTNAME ?? contact.attributes.firstname ?? '');
    const lastName = String(contact.attributes.LASTNAME ?? contact.attributes.lastname ?? '');
    return contact.email.toLowerCase().includes(lower) || firstName.toLowerCase().includes(lower) || lastName.toLowerCase().includes(lower);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search this page…"
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={offset === 0}
            className="rounded-md border border-input p-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span>
            {offset + 1}–{offset + (data?.contacts.length ?? 0)} of {data?.total ?? 0}
          </span>
          <button
            type="button"
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={!data || offset + PAGE_SIZE >= data.total}
            className="rounded-md border border-input p-1.5 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Lists</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td className="px-3 py-2 font-medium">{contact.email}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {contact.listIds.slice(0, 3).map((id) => (
                        <span key={id} className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {listName.get(id) ?? id}
                        </span>
                      ))}
                      {contact.listIds.length > 3 && (
                        <span className="text-xs text-muted-foreground">+{contact.listIds.length - 3} more</span>
                      )}
                      {contact.listIds.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {contact.emailBlacklisted ? (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-400">Blacklisted</span>
                    ) : (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-950/40 dark:text-green-400">Active</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{new Date(contact.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                    No contacts on this page.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
